import { Badge, Box, Tooltip } from "@chakra-ui/react";
import type { PartnershipSelectBadgeKind } from "@common/utils/partnershipPartnerOptions";

export type { PartnershipSelectBadgeKind };

const BADGE_PROPS = {
  fontSize: "10px",
  fontWeight: "semibold",
  lineHeight: "1.2",
  px: 1.5,
  py: "3px",
  borderRadius: "sm",
  flexShrink: 0,
  textTransform: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
} as const;

const DOT_COLOR: Record<PartnershipSelectBadgeKind, string> = {
  approved: "green.500",
  eligible: "gray.400",
  pending: "yellow.400",
  not_eligible: "red.400",
};

function partnershipStatusTooltip(args: {
  kind: PartnershipSelectBadgeKind;
  label: string;
  unusableReason?: string;
}): string {
  if (args.unusableReason?.trim()) {
    return args.unusableReason.trim();
  }
  switch (args.kind) {
    case "approved":
      return "Partnership approved in Meta — ready to run partnership ads";
    case "pending":
      return "Partnership request sent — waiting for partner to accept in Meta";
    case "eligible":
      return "Eligible to invite — send a partnership request or share an approval link";
    case "not_eligible":
      return "Not eligible for partnership ads with the current advertiser setup";
    default:
      return args.label;
  }
}

function badgeVisualProps(
  kind: PartnershipSelectBadgeKind
): Record<string, string> {
  switch (kind) {
    case "approved":
      return { colorScheme: "green" };
    case "pending":
      return { colorScheme: "yellow" };
    case "eligible":
      return {
        bg: "gray.50",
        color: "gray.600",
        borderWidth: "1px",
        borderColor: "gray.200",
      };
    case "not_eligible":
      return { colorScheme: "red" };
    default:
      return { colorScheme: "gray" };
  }
}

export default function PartnershipStatusBadge({
  partnershipSelectBadge,
  partnershipSelectBadgeLabel,
  unusableReason,
  variant = "compact",
}: {
  partnershipSelectBadge?: PartnershipSelectBadgeKind;
  partnershipSelectBadgeLabel?: string;
  unusableReason?: string;
  /** compact = label in menu; dot = color only (selected value) */
  variant?: "compact" | "dot";
}) {
  const kind = partnershipSelectBadge ?? "eligible";
  const label = partnershipSelectBadgeLabel ?? "Eligible";
  const tooltip = partnershipStatusTooltip({ kind, label, unusableReason });

  if (variant === "dot") {
    return (
      <Tooltip label={tooltip}>
        <Box
          as="span"
          w="8px"
          h="8px"
          borderRadius="full"
          bg={DOT_COLOR[kind]}
          flexShrink={0}
          aria-hidden
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip label={tooltip}>
      <Badge {...BADGE_PROPS} {...badgeVisualProps(kind)}>
        {label}
      </Badge>
    </Tooltip>
  );
}
